// The reassessment comparison model — a PURE function over recorded facts
// (F4.6 design §4, revision 2). Nothing here is stored: both sides'
// identities, results and hashes are immutable recorded facts on runs and
// evaluations, and this module derives the comparison at render time.
//
// Classification (ruling §5): the type carries all four addendum §10 values,
// but v1 emits exactly two — `identical_definition` when EVERY identity fact
// matches, `not_comparable` otherwise. The two intermediate values require
// reviewed semantic mapping infrastructure that does not exist; emitting
// them without it would be fabrication. No score, finding or criterion
// movement renders under `not_comparable`.
//
// Refusal matrix (Amendment 3): withdrawn/superseded/ineligible sides refuse
// movement while the identity panels keep rendering wherever tenant-safe
// facts exist, so every refusal stays explainable and auditable. Unknown or
// unreadable classification input collapses to `not_comparable`, never a
// partial movement view.

import type {
  BlockResult,
  CapApplication,
  EvaluationResult,
  EvaluationRow,
  EvaluationSnapshot,
  Finding,
  FormalVerdict,
  Run,
} from "./types";

// ---- Classification -------------------------------------------------------

export type Classification =
  | "identical_definition"
  | "compatible_definition" // declared, never emitted in v1
  | "partially_comparable" //  declared, never emitted in v1
  | "not_comparable";

/** One identity fact of one side, paired for display and comparison. */
export interface IdentityFact {
  label: string;
  baseline: string | null;
  current: string | null;
  matches: boolean;
}

// ---- Sides ----------------------------------------------------------------

export type SideCurrency =
  | { kind: "current" }
  | { kind: "stale" }
  | { kind: "unknown"; reason: string };

export interface ComparisonSideInput {
  run: Run;
  /** The latest COMPARISON-ELIGIBLE evaluation (evaluationEligibility.ts),
   *  or null when the run has none. */
  evaluation: EvaluationRow | null;
  /** Null when there is no evaluation to be current against. */
  currency: SideCurrency | null;
}

/** The C5 identity panel of one side — renders in EVERY outcome. */
export interface SideIdentityPanel {
  runId: string;
  runTitle: string | null;
  runState: Run["state"];
  runCreatedAt: string;
  instrumentVersionId: string;
  definitionHash: string;
  evaluation: {
    id: string;
    seq: number;
    evaluatedAt: string;
    policyId: string;
    policyHash: string;
    engineContractVersion: string;
    canonicalization: string;
    hashAlgorithm: string;
    inputHash: string;
    resultHash: string;
    qualificationStatus: EvaluationRow["qualification_status"];
  } | null;
}

// ---- Movement -------------------------------------------------------------

export interface VerdictSide {
  verdict: FormalVerdict | null;
  withheld: string | null;
}

export interface DimensionMovement {
  dimension: string;
  title: string;
  baselineMean: number | null;
  baselineCount: number;
  currentMean: number | null;
  currentCount: number;
  /** Present only when both means exist. */
  delta: number | null;
  /** The denominators differ — the means are not commensurate claims. */
  denominatorChanged: boolean;
}

export interface CriterionTransition {
  criterionId: string;
  fromApplicability: string;
  toApplicability: string;
  fromScore: number | null;
  toScore: number | null;
  /** Present only when both sides carry a score. */
  scoreDelta: number | null;
}

export interface BlockMovement {
  blockId: string;
  title: string;
  baselineLevel: number | null;
  currentLevel: number | null;
  baselineRaw: number | null;
  currentRaw: number | null;
  /** Present only when both raw scores exist — the DECIMAL delta (ADR-23:
   *  movement inside a level must be visible). */
  rawDelta: number | null;
  baselineCaps: CapApplication[];
  currentCaps: CapApplication[];
  /** A cap present on the baseline no longer applies — labelled separately
   *  from underlying score change (ruling §6). */
  capReleased: boolean;
  capIntroduced: boolean;
  /** Criterion-level state transitions and score deltas; applicability
   *  changes are STATE changes, never score deltas. */
  criterionChanges: CriterionTransition[];
}

/** A kernel finding matched across sides by its stable kernel key. A
 *  PERSISTING finding retains both sides' severity and text (gate 30):
 *  policy-stable identity must not conceal a changed kernel expression. */
export interface FindingComparison {
  key: string;
  bucket: "resolved" | "persisting" | "new";
  baseline: Finding | null;
  current: Finding | null;
  changedExpression: boolean;
}

export interface MovementModel {
  overall: {
    baselineMean: number | null;
    currentMean: number | null;
    baselineRaw: number | null;
    currentRaw: number | null;
    /** Decimal delta of the raw means, when both exist. */
    rawDelta: number | null;
    baselineBlockCount: number;
    currentBlockCount: number;
  };
  verdicts: {
    baseline: VerdictSide;
    current: VerdictSide;
    /** Present ONLY when both sides carry a formal verdict (ADR-30). */
    movement: { from: number; to: number } | null;
  };
  coverage: {
    baseline: EvaluationResult["overall"]["coverage"];
    current: EvaluationResult["overall"]["coverage"];
  };
  evidence: {
    baseline: EvaluationResult["overall"]["evidence"];
    current: EvaluationResult["overall"]["evidence"];
  };
  dimensions: DimensionMovement[];
  blocks: BlockMovement[];
  findings: FindingComparison[];
}

// ---- Outcome --------------------------------------------------------------

export type ComparisonOutcome =
  | { kind: "movement"; classification: "identical_definition"; movement: MovementModel }
  | { kind: "refused"; reason: "current_withdrawn" }
  | { kind: "refused"; reason: "baseline_withdrawn" }
  | { kind: "refused"; reason: "baseline_superseded"; successorRunId: string | null }
  | { kind: "refused"; reason: "baseline_missing_evaluation" } // invariant failure
  | { kind: "refused"; reason: "nothing_to_compare_yet" }
  | { kind: "not_comparable"; mismatches: IdentityFact[] };

export interface ComparisonModel {
  baseline: { panel: SideIdentityPanel; currency: SideCurrency | null };
  current: { panel: SideIdentityPanel; currency: SideCurrency | null };
  /** OQ2: visibly provisional whenever the current run is not finalized or
   *  published. */
  provisional: boolean;
  identityFacts: IdentityFact[];
  outcome: ComparisonOutcome;
}

// ---- Builders -------------------------------------------------------------

function panelOf(side: ComparisonSideInput): SideIdentityPanel {
  const e = side.evaluation;
  return {
    runId: side.run.id,
    runTitle: side.run.title,
    runState: side.run.state,
    runCreatedAt: side.run.created_at,
    instrumentVersionId: side.run.instrument_version_id,
    definitionHash: side.run.definition_hash,
    evaluation: e
      ? {
          id: e.id,
          seq: e.seq,
          evaluatedAt: e.evaluated_at,
          policyId: e.policy_id,
          policyHash: e.policy_hash,
          engineContractVersion: e.engine_contract_version,
          canonicalization: e.canonicalization,
          hashAlgorithm: e.hash_algorithm,
          inputHash: e.input_hash,
          resultHash: e.result_hash,
          qualificationStatus: e.qualification_status,
        }
      : null,
  };
}

/** The approved identity facts (ruling §5): instrument version, definition
 *  hash, policy identity and hash, engine contract, canonicalization, hash
 *  algorithm. A missing or blank value on either side can never read as a
 *  match — unknown input is `not_comparable`. */
function identityFactsOf(
  baseline: ComparisonSideInput,
  current: ComparisonSideInput,
): IdentityFact[] {
  const fact = (
    label: string,
    b: string | null | undefined,
    c: string | null | undefined,
  ): IdentityFact => {
    const bv = b && b.trim() !== "" ? b : null;
    const cv = c && c.trim() !== "" ? c : null;
    return { label, baseline: bv, current: cv, matches: bv !== null && cv !== null && bv === cv };
  };
  return [
    fact("Instrument version", baseline.run.instrument_version_id, current.run.instrument_version_id),
    fact("Definition hash", baseline.run.definition_hash, current.run.definition_hash),
    fact("Policy id", baseline.evaluation?.policy_id, current.evaluation?.policy_id),
    fact("Policy hash", baseline.evaluation?.policy_hash, current.evaluation?.policy_hash),
    fact("Engine contract", baseline.evaluation?.engine_contract_version, current.evaluation?.engine_contract_version),
    fact("Canonicalization", baseline.evaluation?.canonicalization, current.evaluation?.canonicalization),
    fact("Hash algorithm", baseline.evaluation?.hash_algorithm, current.evaluation?.hash_algorithm),
  ];
}

const resultOf = (row: EvaluationRow): EvaluationResult =>
  (row.result as EvaluationSnapshot).result;

function dimensionMovement(b: EvaluationResult, c: EvaluationResult): DimensionMovement[] {
  const byId = new Map(b.dimensions.map((d) => [d.dimension, d]));
  const out: DimensionMovement[] = [];
  for (const cd of c.dimensions) {
    const bd = byId.get(cd.dimension);
    out.push({
      dimension: cd.dimension,
      title: cd.title,
      baselineMean: bd?.mean ?? null,
      baselineCount: bd?.criterion_count ?? 0,
      currentMean: cd.mean,
      currentCount: cd.criterion_count,
      delta:
        bd?.mean != null && cd.mean != null
          ? Number((cd.mean - bd.mean).toFixed(2))
          : null,
      denominatorChanged: bd != null && bd.criterion_count !== cd.criterion_count,
    });
  }
  return out;
}

function criterionChanges(b: BlockResult, c: BlockResult): CriterionTransition[] {
  const byId = new Map(b.criteria.map((x) => [x.criterion_id, x]));
  const out: CriterionTransition[] = [];
  for (const cc of c.criteria) {
    const bc = byId.get(cc.criterion_id);
    if (!bc) continue; // identical definition: absence would be a data defect
    const applicabilityChanged = bc.applicability !== cc.applicability;
    const scoreDelta =
      bc.score != null && cc.score != null && bc.score !== cc.score
        ? Number((cc.score - bc.score).toFixed(2))
        : null;
    if (applicabilityChanged || scoreDelta !== null) {
      out.push({
        criterionId: cc.criterion_id,
        fromApplicability: bc.applicability,
        toApplicability: cc.applicability,
        fromScore: bc.score,
        toScore: cc.score,
        // An applicability change is a STATE change; a score delta is only a
        // score delta when both sides actually scored.
        scoreDelta: applicabilityChanged ? null : scoreDelta,
      });
    }
  }
  return out;
}

function blockMovement(b: EvaluationResult, c: EvaluationResult): BlockMovement[] {
  const byId = new Map(b.blocks.map((x) => [x.block_id, x]));
  const out: BlockMovement[] = [];
  for (const cb of c.blocks) {
    const bb = byId.get(cb.block_id);
    if (!bb) continue;
    out.push({
      blockId: cb.block_id,
      title: cb.title,
      baselineLevel: bb.calculated_maturity_level,
      currentLevel: cb.calculated_maturity_level,
      baselineRaw: bb.calculated_raw_score,
      currentRaw: cb.calculated_raw_score,
      rawDelta:
        bb.calculated_raw_score != null && cb.calculated_raw_score != null
          ? Number((cb.calculated_raw_score - bb.calculated_raw_score).toFixed(2))
          : null,
      baselineCaps: bb.caps_applied,
      currentCaps: cb.caps_applied,
      capReleased: bb.caps_applied.length > 0 && cb.caps_applied.length === 0,
      capIntroduced: bb.caps_applied.length === 0 && cb.caps_applied.length > 0,
      criterionChanges: criterionChanges(bb, cb),
    });
  }
  return out;
}

function findingComparison(b: EvaluationResult, c: EvaluationResult): FindingComparison[] {
  // Matched ONLY by the stable kernel finding key (`block.id:code` — the
  // finding's own id), the same identity the promotion model is keyed on.
  const bById = new Map(b.findings.map((f) => [f.id, f]));
  const cById = new Map(c.findings.map((f) => [f.id, f]));
  const out: FindingComparison[] = [];
  for (const [key, bf] of bById) {
    const cf = cById.get(key) ?? null;
    out.push({
      key,
      bucket: cf ? "persisting" : "resolved",
      baseline: bf,
      current: cf,
      changedExpression: cf != null && (cf.severity !== bf.severity || cf.text !== bf.text),
    });
  }
  for (const [key, cf] of cById) {
    if (!bById.has(key))
      out.push({ key, bucket: "new", baseline: null, current: cf, changedExpression: false });
  }
  const order = { resolved: 0, persisting: 1, new: 2 } as const;
  out.sort((x, y) => order[x.bucket] - order[y.bucket] || x.key.localeCompare(y.key));
  return out;
}

function movementModel(bRow: EvaluationRow, cRow: EvaluationRow): MovementModel {
  const b = resultOf(bRow);
  const c = resultOf(cRow);
  return {
    overall: {
      baselineMean: b.overall.mean_maturity_level,
      currentMean: c.overall.mean_maturity_level,
      baselineRaw: b.overall.mean_raw_score,
      currentRaw: c.overall.mean_raw_score,
      rawDelta:
        b.overall.mean_raw_score != null && c.overall.mean_raw_score != null
          ? Number((c.overall.mean_raw_score - b.overall.mean_raw_score).toFixed(2))
          : null,
      baselineBlockCount: b.overall.block_count,
      currentBlockCount: c.overall.block_count,
    },
    verdicts: {
      baseline: { verdict: b.formal_verdict, withheld: b.formal_verdict_withheld },
      current: { verdict: c.formal_verdict, withheld: c.formal_verdict_withheld },
      movement:
        b.formal_verdict && c.formal_verdict
          ? { from: b.formal_verdict.mean_maturity_level, to: c.formal_verdict.mean_maturity_level }
          : null,
    },
    coverage: { baseline: b.overall.coverage, current: c.overall.coverage },
    evidence: { baseline: b.overall.evidence, current: c.overall.evidence },
    dimensions: dimensionMovement(b, c),
    blocks: blockMovement(b, c),
    findings: findingComparison(b, c),
  };
}

/**
 * The comparison, from two pre-selected sides.
 *
 * Callers select each side's evaluation with `selectEligibleEvaluation`
 * (the shared Amendment 2 predicate) and compute currencies with the
 * standing fingerprint rules; this function is pure over its inputs.
 *
 * Refusal order (Amendment 3): lifecycle first, then evaluation presence,
 * then classification. The identity panels are built unconditionally.
 */
export function buildComparisonModel(
  baseline: ComparisonSideInput,
  current: ComparisonSideInput,
): ComparisonModel {
  const identityFacts = identityFactsOf(baseline, current);
  const base: Omit<ComparisonModel, "outcome"> = {
    baseline: { panel: panelOf(baseline), currency: baseline.currency },
    current: { panel: panelOf(current), currency: current.currency },
    provisional: current.run.state !== "finalized" && current.run.state !== "published",
    identityFacts,
  };

  const refuse = (outcome: ComparisonOutcome): ComparisonModel => ({ ...base, outcome });

  // 1. Lifecycle refusals.
  if (current.run.state === "withdrawn")
    return refuse({ kind: "refused", reason: "current_withdrawn" });
  if (baseline.run.state === "withdrawn")
    return refuse({ kind: "refused", reason: "baseline_withdrawn" });
  if (baseline.run.state === "superseded")
    return refuse({
      kind: "refused",
      reason: "baseline_superseded",
      successorRunId: baseline.run.superseded_by,
    });

  // 2. Evaluation presence. The baseline passed a finalization gate that
  //    required an eligible evaluation — absence is an INVARIANT FAILURE,
  //    not an empty state. The current side simply has nothing to compare
  //    yet, which is honest and distinct from `not_comparable` (the
  //    classification was never computed).
  if (baseline.evaluation === null)
    return refuse({ kind: "refused", reason: "baseline_missing_evaluation" });
  if (current.evaluation === null)
    return refuse({ kind: "refused", reason: "nothing_to_compare_yet" });

  // 3. Classification. ANY mismatch — including a missing or unreadable
  //    fact — is `not_comparable`; never a partial movement view.
  const mismatches = identityFacts.filter((f) => !f.matches);
  if (mismatches.length > 0)
    return refuse({ kind: "not_comparable", mismatches });

  return {
    ...base,
    outcome: {
      kind: "movement",
      classification: "identical_definition",
      movement: movementModel(baseline.evaluation, current.evaluation),
    },
  };
}
